import type { QueryClient } from "@tanstack/react-query";

/**
 * Single source of truth for the React Query keys that feed the map surfaces
 * (pins, filter counts and the KPI/показатели strip) on the Orders and Routes
 * maps.
 *
 * Both BUG-334 (Orders map) and BUG-335 (Routes map) were the same class of
 * defect: a mutation that changes route/order assignment refreshed some caches
 * but forgot the ones feeding the KPI aggregates, so the strip kept showing
 * pre-mutation figures until a full page reload.
 *
 * The KPI values are backend whole-list aggregates (`status_counts` + `sums`,
 * Sprint 157) attached to the LIST responses, NOT a page-local reduce. So the
 * fix is to invalidate every list/map query that carries those aggregates once
 * the mutation (or the async build/reoptimize it triggers) has actually
 * COMPLETED — not when a POST merely returns a task id.
 *
 * Keys covered:
 *   - `["orders"]`      — Orders list feeds the Orders-map pins, filter counts
 *                          AND the KPI strip (single query, prefix match).
 *   - `["routes"]`      — Routes table list.
 *   - `["routes-map"]`  — Routes-map pins AND the Routes-map KPI strip (a
 *                          SEPARATE key from `["routes"]`; the BUG-297 lesson).
 *   - `["unassigned-orders"]` — the unassigned pool (map panel + badge).
 *
 * Prefer this helper over sprinkling `invalidateQueries` calls so the next
 * mutation on a map surface cannot silently forget one of the KPI keys.
 */
export function invalidateMapSurfaces(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ["orders"] });
  void queryClient.invalidateQueries({ queryKey: ["routes"] });
  void queryClient.invalidateQueries({ queryKey: ["routes-map"] });
  void queryClient.invalidateQueries({ queryKey: ["unassigned-orders"] });
}
